import type { TransferSearchListItemProps } from './index.types';
import React, { useCallback } from 'react';
import { formatNumber } from 'Utils/phoneUtil';
import { highlightText } from '~/lib/ui/autocomplete/utils';
import { cn } from '~/lib/utils';
import { TransferContactAvatar } from '../TransferContactAvatar';
import { CONTACT_TYPE_LABELS } from './index.constants';

/**
 * A single contact row in the transfer search results list.
 *
 * Renders the contact avatar (with live presence), display name,
 * contact type label, and primary phone number — all with search
 * term highlighting.
 */
export const TransferSearchListItem = ({
  contact,
  searchTerm,
  onSelectContact,
  testID,
}: TransferSearchListItemProps): React.ReactElement => {
  const componentTestId = testID
    ? `${testID}-contact-list-item`
    : 'contact-list-item';
  const trimmedQuery = searchTerm.trim();

  const handleClick = useCallback(() => {
    onSelectContact(contact);
  }, [onSelectContact, contact]);

  const primaryPhone = contact.phoneNumbers[0];
  const showPhoneNumber = Boolean(trimmedQuery && primaryPhone?.number);
  const typeLabel = CONTACT_TYPE_LABELS[contact.contactType] ?? 'External';

  return (
    <button
      type="button"
      id={contact?.id}
      onClick={handleClick}
      className={cn(
        'w-full flex items-center gap-[10px] px-[16px] py-[10px] rounded-md',
        'hover:bg-accent transition-colors cursor-pointer text-left'
      )}
      role="listitem"
      aria-label={`Transfer to ${contact.displayName}`}
      data-testid={componentTestId}
    >
      {contact && <TransferContactAvatar contact={contact} />}

      <div className="flex flex-col min-w-0 flex-1">
        {contact.displayName && (
          <h3 className="text-[16px] leading-[22px] font-normal text-foreground truncate">
            {highlightText(contact.displayName, trimmedQuery)}
          </h3>
        )}

        {typeLabel && (
          <p className="text-[12px] leading-[16px] font-normal text-foreground truncate">
            {typeLabel} contact
          </p>
        )}

        {showPhoneNumber && (
          <p className="text-[12px] leading-[16px] font-normal text-foreground truncate">
            {primaryPhone.label}:{' '}
            {highlightText(
              primaryPhone.label === 'Office'
                ? primaryPhone.number
                : formatNumber(primaryPhone.number),
              trimmedQuery
            )}
          </p>
        )}
      </div>
    </button>
  );
};

TransferSearchListItem.displayName = 'TransferSearchListItem';
